/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { styleMap } from 'lit-html/directives/style-map.js';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { BasicOverlayMixin } from '../../utils/basic-overlay-mixin.js';
import { getOverlayPlacementInfo } from '../../utils/getOverlayPlacement.js';
import { KEYCODE } from '../../utils/keycode.js';
import { ArrowPosition } from './popover-arrow-positioner.js';

/**
 * @prop {String} label - Defines the label of the popover that will be displayed on top of the content. PLEASE-REVIEW
 * @prop {Boolean} visible - Defines if the popover is shown (true) or not (false)
 * @prop {String} target - Defines the id of the tooltip for which the tooltip will be shown
          Should be given as an id to the component for which a tooltip is required
 * @prop {String} position - Default 'top'. Defines where the tooltip will be shown
          Accepted values are - top, bottom, left, right, top-right, top-left, bottom-left, bottom-right, left-up, left-down, right-up, right-down
 * @prop {Boolean} sticky - If set to `true`, the popover will not close when the user clicks on the page. It can be closed through actions present in the popover
 * @prop {String} type - Defines the type of popover that will be displayed. Accepted values are error, alert and confirmation
 * @prop {Boolean} mobile - If set to `true`, the popover will show styles applicable to mobile view
 * @prop {Boolean} passive - 	If set to `true`, the popover will show a pointing arrow.
 * @prop {String} scrollAction - Default 'refit'. Decides the behavior of popover when scrolled. Accepted values are 'refit' and 'hide'
 * @prop {Boolean} alwaysStayWithin - Forces popover to stay in visible viewport when enabled
 * @prop {Function} customPlacementLogic - Pluggable logic to handle placement of popover.
 * @prop {Number} overlayTargetDistance - distance to overlay target.
 * @prop {Boolean} subMenu - *hide
 * @prop {DomNode} viewport - Default: document.body. Reference viewport node used for placement.
 * @prop {Boolean} suppressPlacementWarning - Will suppress placement warnings when enabled.
 * @prop {Boolean} blurBackground - If set to `true`, the popover will have a semi-transparent background.
 * @prop {Number} viewportPadding - Min padding that will be kept from viewport boundaries.
 * @slot footer - Takes buttons as children.
 * @slot functions - Takes icons as children.
 */
export class SHPopover extends BasicOverlayMixin(LitElement) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      target: { type: String, reflect: true },
      visible: { type: Boolean, reflect: true },
      sticky: { type: Boolean, reflect: true },
      subMenu: { type: Boolean, reflect: true, attribute: 'sub-menu' },
      position: { type: String, reflect: true },
      type: { type: String, reflect: true },
      passive: { type: Boolean, reflect: true },
      mobile: { type: Boolean, reflect: true },
      scrollAction: { type: String, reflect: true, attribute: 'scroll-action' },
      blurBackground: { type: Boolean, reflect: true, attribute: 'blur-background' },
      _emptyFunction: { type: Boolean },
      _emptyFooter: { type: Boolean },
      customPlacementLogic: {
        type: Object,
        reflect: true,
        attribute: 'custom-placement-logic',
      },
      suppressPlacementWarning: {
        type: Boolean,
        reflect: true,
        attribute: 'suppress-placement-warning',
      },
      alwaysStayWithin: {
        type: Boolean,
        reflect: true,
        attribute: 'always-stay-within',
      },
      _targetEl: {
        type: Object,
      },
      viewport: {
        type: Object,
        reflect: true,
      },
      viewportPadding: {
        type: Number,
        reflect: true,
        attribute: 'viewport-padding',
      },
      overlayTargetDistance: {
        type: Number,
        reflect: true,
        attribute: 'overlay-target-distance',
      },
      _arrowX: {
        type: Number,
      },
      _arrowY: {
        type: Number,
      },
      _arrowRotation: {
        type: Number,
      },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          font: var(--body-1);
          color: var(--text-primary);
          display: block;
          position: fixed;
          opacity: 0;
          visibility: hidden;
          background-color: var(--base-4);
          min-width: 12.5rem;
          width: 15.5rem;
          min-height: auto;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
          box-sizing: border-box;
          border-radius: 0.125rem;
          margin-top: 0rem !important;
          box-shadow: var(--shadow-overlay);
          transition: var(--transition-time) all ease-in-out, 0s top, 0s left, 0s width,
            0s min-width;
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
          -moz-osx-font-smoothing: grayscale;
          padding: 1rem;
          z-index: 999;
          pointer-events: none;
          overflow-wrap: break-word;
        }
        :host(:not([mobile])[blur-background]) {
          background-color: rgba(var(--blur-background-1), var(--opacity-3));
          backdrop-filter: blur(var(--blur-1));
          -webkit-backdrop-filter: blur(var(--blur-1));
        }
        :host([label]) {
          padding-top: 1rem;
        }

        :host(:not([mobile])) .popover-wrapper {
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        :host(:not([passive])) .arrow {
          display: none;
        }
        :host([passive]) .arrow {
          position: absolute;
          border-width: 0.375rem;
          border-style: solid;
          border-color: transparent transparent var(--base-4) transparent;
          border-bottom-width: 0.5rem;
          transform-origin: 0.375rem 0.875rem;
        }
        :host(:not([mobile])[passive][blur-background]) .arrow {
          border-color: transparent transparent rgba(var(--blur-background-1), var(--opacity-3))
            transparent;
        }
        @-moz-document url-prefix() {
          :host(:not([mobile])[blur-background]) {
            background-color: rgba(var(--blur-background-1), var(--opacity-2));
          }
          :host(:not([mobile])[passive][blur-background]) .arrow {
            border-color: transparent transparent rgba(var(--blur-background-1), var(--opacity-2))
              transparent;
          }
        }
        .header-wrapper {
          display: flex;
          margin-bottom: 1rem;
        }
        .footer-wrapper {
          display: flex;
          justify-content: flex-end;
          margin-top: 1rem;
        }
        .header-wrapper > ::slotted(*),
        .footer-wrapper > ::slotted(*) {
          margin-left: 0.5rem;
        }
        :host(:not([label])) .header-wrapper {
          display: none;
        }
        .popover-label {
          font: var(--header-2);
          color: var(--text-primary);
          display: block;
          line-height: 1.5rem;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        :host([visible]) {
          visibility: visible;
          opacity: 1;
          pointer-events: all;
        }
        :host([sub-menu]) {
          transform: translateY(-0.5rem);
        }
        .header-wrapper sh-icon {
          margin-right: 0.5rem;
        }
        :host([mobile]) {
          position: fixed;
          top: 0rem !important;
          left: 0rem !important;
          height: 100vh;
          background: rgba(0, 0, 0, var(--opacity-4));
          z-index: 999;
          width: 100vw;
          padding: 0;
          transition: var(--transition-time) all ease-in-out,
            0s height ease-in-out var(--transition-time),
            0s max-height ease-in-out var(--transition-time);
        }
        :host([mobile]) .popover-wrapper {
          position: absolute;
          width: calc(100vw - 2rem);
          padding: 1rem;
          background: var(--base-4);
          bottom: 0rem;
          max-height: calc(100% - 6.5rem);
          overflow: auto;
        }
        /* slot styles */
        :host(:not([label]):not([type])) .popover-wrapper[empty-function] .header-wrapper,
        .popover-wrapper[empty-footer] .footer-wrapper {
          display: none;
        }
        slot:not([name]) {
          flex: 1;
          line-height: 1.25rem;
        }
      `,
    ];
  }

  render() {
    return html`
      <div
        class="popover-wrapper"
        ?empty-function="${this._emptyFunction}"
        ?empty-footer="${this._emptyFooter}"
      >
        <div class="header-wrapper">
          ${this.type === 'error' ? html` <sh-icon icon="error" size="s"></sh-icon>` : ''}
          ${this.type === 'alert' || this.type === 'warning'
            ? html` <sh-icon icon="warning" size="s"></sh-icon>`
            : ''}
          ${this.type === 'confirmation' || this.type === 'success'
            ? html` <sh-icon icon="success" size="s"></sh-icon>`
            : ''}
          ${this.label || this.label === ''
            ? html` <div class="popover-label">${this.label}</div>`
            : ''}
          <slot
            name="functions"
            @slotchange="${(e) => this._updateSlotFlag(e.target, '_emptyFunction')}"
          ></slot>
        </div>
        <slot></slot>
        <div class="footer-wrapper">
          <slot
            name="footer"
            id="footer"
            @slotchange="${(e) => this._updateSlotFlag(e.target, '_emptyFooter')}"
          ></slot>
        </div>
      </div>
      <div class="arrow" style="${styleMap(this._getArrowDimensions())}"></div>
    `;
  }

  constructor() {
    super();
    this._emptyFooter = true;
    this._emptyFunction = true;
    this.position = 'top';
    this.scrollAction = 'refit';
    this.overlayTargetDistance = 0.5;
    this.viewportPadding = 0.5;
    this.viewport = document.body;
  }

  _updateSlotFlag(slot, emptySlotFlag) {
    const numberOfNodes = slot.assignedNodes({
      flatten: true,
    }).length;
    if (numberOfNodes === 0) {
      this[emptySlotFlag] = true;
    } else {
      this[emptySlotFlag] = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.documentClickListener = this.documentClickActions.bind(this);
    this.documentKeydownListener = this.documentKeyDownEventActions.bind(this);
    this._subMenuTargetMouseOverListener = this.show.bind(this);
    this._subMenuTargetMouseOutListener = this.hide.bind(this);
    this._mouseOverListener = this._mouseOverActions.bind(this);
    this._mouseOutListener = this._mouseOutActions.bind(this);
    document.body.addEventListener('click', this.documentClickListener);
    if (this.scrollAction === 'hide') {
      document.body.addEventListener('keydown', this.documentKeydownListener);
    }
    this.onkeyup = (e) => {
      if (
        (e.keyCode === KEYCODE.SPACE ||
          e.keyCode === KEYCODE.ENTER ||
          e.keyCode === KEYCODE.ESCAPE) &&
        this.visible &&
        !this.sticky
      ) {
        this.hide();
      }
    };
    if (!this.mobile) {
      /** @ignore */
      this.resizeListener = this.handleMobile.bind(this);
      this.resizeListener();
    }
    window.addEventListener('resize', this.resizeListener);
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('target')) {
      this._invokerNode = this._targetEl;
      this._attachNode = this._targetEl;
    }
    if (changedProperties.has('position') || changedProperties.has('passive')) {
      if (this._canPositionOverlay()) {
        this._setOverlayPlacement(this._targetEl);
      }
    }
    const listOfProperties = [
      'label',
      'target',
      'visible',
      'sticky',
      'subMenu',
      'position',
      'type',
      'passive',
      'mobile',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  handleMobile() {
    this.mobile = document.documentElement.clientWidth < 640;
  }

  documentClickActions(e) {
    let targetElement = this._targetEl;
    if (e.composedPath().indexOf(targetElement) === -1) {
      if (!this.sticky) {
        this.hide();
      }
    } else {
      if (this.visible) {
        this.hide();
      } else {
        this.show();
      }
    }
  }

  get _targetEl() {
    const target = this.target;
    if (target) {
      if (typeof target === 'string') {
        const targetElement = this.parentElement
          ? this.parentElement.querySelector(`[id="${target}"]`) || document.getElementById(target)
          : document.getElementById(target);
        return targetElement;
      } else if (target instanceof HTMLElement) {
        return target;
      } else {
        throw new Error(
          `\n\nThe "target" property is not of valid type.` +
            `\nThe "target" property can take string value corresponding to the id of the target.` +
            `\nIt can also have its value equal to a DOM Node reference (instance of HTMLElement)\n`
        );
      }
    }
  }

  documentKeyDownEventActions(e) {
    if (this.scrollAction === 'hide' && !this.sticky) {
      if (
        e.keyCode === KEYCODE.ARROW_UP ||
        e.keyCode === KEYCODE.ARROW_DOWN ||
        e.keyCode === KEYCODE.SPACE
      ) {
        this.hide();
      }
    }
  }

  _mouseOverActions() {
    const parentPopover = this._targetEl.parentElement;
    parentPopover.subMenu ? parentPopover.show() : '';
    this.show();
  }

  _mouseOutActions() {
    const parentPopover = this._targetEl.parentElement;
    parentPopover.subMenu ? parentPopover.hide() : '';
    this.hide();
  }

  _addHoverEvents(_targetEl) {
    _targetEl.addEventListener('mouseover', this._subMenuTargetMouseOverListener);
    _targetEl.addEventListener('mouseout', this._subMenuTargetMouseOutListener);
    this.addEventListener('mouseover', this._mouseOverListener);
    this.addEventListener('mouseout', this._mouseOutListener);
  }

  _removeHoverEvents(_targetEl) {
    _targetEl.removeEventListener('mouseover', this._subMenuTargetMouseOverListener);
    _targetEl.removeEventListener('mouseout', this._subMenuTargetMouseOutListener);
    this.removeEventListener('mouseover', this._mouseOverListener);
    this.removeEventListener('mouseout', this._mouseOutListener);
  }

  show() {
    this._invokerNode = this._targetEl;
    this._attachNode = this._targetEl;
    this.visible = true;
  }

  hide() {
    this.visible = false;
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.scrollHandler);
    document.body.removeEventListener('click', this.documentClickListener);
    document.body.removeEventListener('keydown', this.documentKeydownListener);
    window.removeEventListener('resize', this.resizeListener);
    super.disconnectedCallback();
  }

  _setOverlayPlacement(attachNode) {
    if (!this.customPlacementLogic) {
      const targetIsWorklineItem = attachNode.tagName === 'SH-WORKLINE-ITEM';
      const viewportPadding = this.passive
        ? this.viewportPadding >= 1
          ? this.viewportPadding
          : 1
        : this.viewportPadding;
      const overlayTargetDistance = this.passive
        ? this.overlayTargetDistance >= 1
          ? this.overlayTargetDistance
          : 1
        : this.overlayTargetDistance;
      let worklinePlacementAlgorithm = {
        top: [
          {
            from: 'top',
            to: 'top-diag-left',
            increment: 'overlayRight',
            incrementBy: -1,
          },
          {
            from: 'top',
            to: 'top-diag-right',
            increment: 'overlayLeft',
            incrementBy: 1,
          },
        ],
      };
      // adjusting the viewport and the left, width and right
      // of the final viewport to make the viewport have the
      // left and right equal to the workline-wrapper's left and right
      // when the target is a workline-item so as to make the popover
      // stop at the left and right of workline-wrapper and make the
      // arrow point towards left or right when workline item goes
      // beneath the left/right slot of the workline.
      const viewport = targetIsWorklineItem
        ? attachNode.closest('sh-workline').shadowRoot.querySelector('.workline-wrapper')
        : this.viewport;
      const thisViewportRect = this.viewport.getBoundingClientRect();
      const viewPortRect = JSON.parse(JSON.stringify(thisViewportRect));
      viewPortRect.left = viewport.getBoundingClientRect().left;
      viewPortRect.width = viewport.getBoundingClientRect().width;
      viewPortRect.right = viewport.getBoundingClientRect().right;
      const placementAlgorithm = targetIsWorklineItem ? worklinePlacementAlgorithm : null;
      const suppressPlacementWarning = targetIsWorklineItem ? true : this.suppressPlacementWarning;
      const alwaysStayWithin = targetIsWorklineItem ? true : this.alwaysStayWithin;
      const placementInfo = getOverlayPlacementInfo(
        this.position,
        this.getBoundingClientRect(),
        attachNode.getBoundingClientRect(),
        viewPortRect,
        overlayTargetDistance,
        viewportPadding,
        placementAlgorithm,
        placementAlgorithm,
        suppressPlacementWarning
      );
      if (placementInfo.targetIsOutsideViewport && !alwaysStayWithin) {
        this.visible = false;
        if (!suppressPlacementWarning) {
          console.warn(
            'Since the effective target is outside the effective viewport, the popover is hidden'
          );
        }
      } else {
        this.style.left = placementInfo.requiredLeft + 'px';
        this.style.top = placementInfo.requiredTop + 'px';
      }
    } else {
      this.customPlacementLogic();
    }
    if (this.passive) {
      const arrowPositionData = new ArrowPosition(attachNode, this);
      this._arrowX = arrowPositionData.arrowPositionX;
      this._arrowY = arrowPositionData.arrowPositionY;
      this._arrowRotation = arrowPositionData.arrowRotation;
    }
  }

  _canPositionOverlay() {
    return this.position && this.visible && !this.mobile && this._targetEl;
  }

  _doSomethingWithOldInvokerNode(invokerNode) {
    this._removeHoverEvents(invokerNode);
  }

  _doSomethingWithNewInvokerNode(invokerNode) {
    if (this.subMenu) {
      this._addHoverEvents(invokerNode);
    }
  }
  _getArrowDimensions() {
    return {
      left: `${this._arrowX}rem`,
      top: `${this._arrowY}rem`,
      transform: `rotate(${this._arrowRotation}deg)`,
    };
  }
}

customElements.define('sh-popover', SHPopover);
